import React, { useState, useEffect, useRef, useContext } from "react";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSearch, FaPlus, FaTrash, FaPrint, FaFileExport, FaSave, FaWhatsapp, FaCheck, FaTimes } from "react-icons/fa";
import BillReceipt, { printBillReceipt } from "./BillReceipt";
import { shareBillViaWhatsApp } from "./BillStatementReceipt";
import AuthContext from "../../../../store/auth-context";
import Loader from "../../../UI/Loader";
import { Modal, Button, Table } from "react-bootstrap";
import { parseHeader, drawReportHeader } from "../../../../util/headerUtil";
import { themeColors } from "../../../../util/themeColors";
import { useTranslation } from "react-i18next";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const NewBill = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;
  const [isLoading, setIsLoading] = useState(false);
  const expenseInput = useRef();
  const amountInput = useRef();
  const quantityInput = useRef();
  const discountInput = useRef();
  const [createdBill, setCreatedBill] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [searchPara, setSearchPara] = useState("");
  const [selectedPatient, setSelectedPatient] = useState({});
  const [totalBill, setTotalBill] = useState(0);
  const [printAfterSave, setPrintAfterSave] = useState(
    authCtx.hospitalData?.IPDPrintBill === true || authCtx.hospitalData?.IPDPrintBill === "true"
  );
  const [amountReceived, setAmountReceived] = useState("");
  const [outstandingAmount, setOutstandingAmount] = useState("");
  const [patientOutstanding, setPatientOutstanding] = useState(null);
  const [doPrint, setDoPrint] = useState(false);
  const [billingDate, setBillingDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idCounter = useRef(1);
  const [paymentType, setPaymentType] = useState("cash");
  const [txnNo, setTxnNo] = useState("");
  const [note, setNote] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [charges, setCharges] = useState([]);
  const [savedBillId, setSavedBillId] = useState(null);

  // Set default quantity to 1 and fetch all charges on mount
  useEffect(() => {
    if (quantityInput.current) {
      quantityInput.current.value = "1";
    }
    fetchAllCharges();
  }, []);

  const fetchAllCharges = async () => {
    const token = getAuthToken();
    const apiChar = getApiChara();
    try {
      const response = await fetch(`${apiChar}/charge-master`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        const data = await response.json();
        setCharges(data || []);
      }
    } catch (error) {
      console.error("Error fetching charges:", error);
    }
  };

  // Handle search input
  const getSerachParaHandler = (e) => setSearchPara(e.target.value);

  // Search patient
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl =
        `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` +
        searchPara;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Could Not fetch details");
      const data = await response.json();
      setPatientData(data);
      setCreatedBill([]);
    } catch (err) {
      toast.error(t("Failed to fetch patient data"));
    } finally {
      setIsLoading(false);
    }
  };

  const [showChargeDropdown, setShowChargeDropdown] = useState(false);
  const [expenseSearch, setExpenseSearch] = useState("");

  const filteredCharges = expenseSearch
    ? charges.filter(c => c.chargeName.toLowerCase().includes(expenseSearch.toLowerCase()))
    : charges;

  const onSearchChargeHandler = (term) => {
    setExpenseSearch(term);
    setShowChargeDropdown(true);
  };

  const handleChargeSelect = (charge) => {
    expenseInput.current.value = charge.chargeName;
    setExpenseSearch(charge.chargeName);
    amountInput.current.value = charge.amount;
    setShowChargeDropdown(false);
  };

  // Add bill item
  const onSubmitBillHandler = () => {
    const expenseName = expenseInput.current.value;
    const amount = parseFloat(amountInput.current.value || 0);
    const quantity = parseFloat(quantityInput.current.value || 1);
    const discount = parseFloat(discountInput.current.value || 0);

    if (!expenseName && !amount && !selectedPatient?.patientId) {
      toast.warning(
        t("Please select a patient and enter expense name, amount and quantity.")
      );
      return;
    } else if (!expenseName) {
      toast.warning(t("Please enter expense name."));
      return;
    } else if (amountInput.current.value === "" || isNaN(amount)) {
      toast.warning(t("Please enter amount."));
      return;
    } else if (quantityInput.current.value === "" || isNaN(quantity) || quantity <= 0) {
      toast.warning(t("Please enter quantity greater than 0."));
      return;
    } else if (!selectedPatient?.patientId) {
      toast.warning(t("Please select a patient."));
      return;
    }

    if (createdBill.some((bill) => bill.expenseName === expenseName)) {
      toast.warning(t("Expense name already exists."));
      return;
    }

    const actualAmount = amount;
    const netAmount = parseFloat(actualAmount * quantity * (1 - discount / 100));

    const billingData = {
      id: idCounter.current++,
      expenseName,
      actualAmount: actualAmount.toFixed(2),
      netAmount: netAmount.toFixed(2),
      quantity,
      totalAmount: (actualAmount * quantity).toFixed(2),
      discount,
      date: billingDate,
    };

    setCreatedBill((prev) => [...prev, billingData]);

    expenseInput.current.value = "";
    amountInput.current.value = "";
    quantityInput.current.value = "1";
    discountInput.current.value = "";
  };

  // Remove bill item
  const removeBillItem = (indexToRemove) => {
    setCreatedBill((prevBill) => {
      const updatedBill = prevBill.filter((_, index) => index !== indexToRemove);
      const refreshedBill = updatedBill.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      idCounter.current = refreshedBill.length + 1;
      return refreshedBill;
    });
  };

  // Update total whenever createdBill changes
  useEffect(() => {
    const total = createdBill.reduce(
      (sum, item) => sum + parseFloat(item.netAmount || 0),
      0
    );
    setTotalBill(parseFloat(total.toFixed(2)));
    setAmountReceived(parseFloat(total.toFixed(2)));
  }, [createdBill]);

  useEffect(() => {
    const received = parseFloat(amountReceived) || 0;
    const outstanding = totalBill - received;
    setOutstandingAmount(outstanding.toFixed(2));
  }, [totalBill, amountReceived]);

  // Close modal
  const onDataCloseHandler = () => {
    setPatientData([]);
    navigate(".");
  };

  // Select patient
  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/billing/outstanding?patientId=${patient.patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch outstanding amount");

      const amount = await response.json();
      setPatientOutstanding(amount);
    } catch (err) {
      console.error("Error fetching outstanding:", err);
      setPatientOutstanding(0);
    } finally {
      setPatientData([]);
    }
  };

  const onAmountReceivedChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setAmountReceived("");
      setOutstandingAmount(totalBill.toFixed(2));
      return;
    }

    const num = parseFloat(value) || 0;
    setAmountReceived(num);

    let outstanding = totalBill - num;
    outstanding = parseFloat(outstanding.toFixed(2));
    setOutstandingAmount(outstanding);
  };

  const onSaveBillHandler = async () => {
    if (!selectedPatient?.patientId) {
      toast.error(t("Patient details required before saving bill"));
      return;
    }
    if (!paymentType) {
      toast.warning(t("Please select a payment type before submitting"));
      return;
    }
    if (!amountReceived && amountReceived !== 0) {
      toast.warning(t("Please enter the amount received before submitting"));
      return;
    }
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }
    setIsSubmitting(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/billing`;

      const formattedBillingDate = `${billingDate.getFullYear()}-${String(
        billingDate.getMonth() + 1
      ).padStart(2, "0")}-${String(billingDate.getDate()).padStart(2, "0")} ${String(
        billingDate.getHours()
      ).padStart(2, "0")}:${String(billingDate.getMinutes()).padStart(2, "0")}:${String(
        billingDate.getSeconds()
      ).padStart(2, "0")}`;
      const payload = {
        id: 0,
        billReceivedById: {
          userId: authCtx.userId,
          role: authCtx.role,
          userName: authCtx.userName,
        },
        patientId: selectedPatient,
        billingDate: formattedBillingDate,
        paymentType: paymentType || "",
        txnNo: txnNo || "",
        note: note,
        totalAmount: totalBill.toFixed(2),
        amountReceived: parseFloat(amountReceived).toFixed(2),
        outstandingAmount: parseFloat(outstandingAmount).toFixed(2),
        userName: authCtx.userName,
        patientName: `${selectedPatient?.firstName || ""} ${selectedPatient?.middleName || ""
          } ${selectedPatient?.lastName || ""}`.trim(),
        serviceItems: createdBill.map((bill) => ({
          serviceDescription: bill.expenseName,
          actualAmount: parseFloat(bill.actualAmount).toFixed(2),
          totalAmount: (bill.actualAmount * bill.quantity).toFixed(2),
          netAmount: parseFloat(bill.netAmount).toFixed(2),
          quantity: bill.quantity,
          discount: bill.discount,
        })),
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        toast.error(t("Failed to save bill"));
        return;
      }

      const result = await response.json();
      const newBillId = result.id;
      setSavedBillId(newBillId);
      toast.success(`${t("Bill Saved successfully!")} (Bill No: ${newBillId})`);

      if (printAfterSave) {
        setDoPrint(true);
      } else {
        resetForm();
      }
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error(t("Failed to save bill"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCreatedBill([]);
    setTotalBill(0);
    setSelectedPatient({});
    idCounter.current = 1;
    setAmountReceived("");
    setOutstandingAmount("");
    setPaymentType("cash");
    setSavedBillId(null);
    setSearchPara("");
    setExpenseSearch("");
    if (expenseInput.current) {
      expenseInput.current.value = "";
    }
  };

  useEffect(() => {
    if (doPrint) {
      printBillReceipt(
        selectedPatient,
        createdBill,
        totalBill,
        amountReceived,
        outstandingAmount,
        billingDate,
        paymentType,
        txnNo,
        authCtx,
        savedBillId
      );
      setDoPrint(false);
      resetForm();
    }
  }, [doPrint]);

  const buildPDF = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    // 🔹 Hospital Header (Standardized)
    const startY = drawReportHeader(doc, hospitalData, "", {
      margin: 0,
      top: 0,
      hideSeparator: true,
      isExport: true,
      contentMargin: 40
    });

    let currentY = startY + 10;

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("BILL RECEIPT", pageWidth / 2, currentY, { align: "center" });

    currentY += 15;

    // Patient Info section
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");

    // Row 1: Name & Date
    doc.text(`Name: ${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`, margin, currentY);
    const dateStr = new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    doc.text(`Date: ${dateStr}`, pageWidth - margin, currentY, { align: "right" });

    currentY += 8;

    // Row 2: Bill No & Mobile
    doc.text(`Bill No: ${savedBillId || "-"}`, margin, currentY);
    doc.text(`Mobile: ${selectedPatient?.mobile || "-"}`, pageWidth - margin, currentY, { align: "right" });

    currentY += 8;

    doc.autoTable({
      startY: currentY,
      head: [
        ["Sr. No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
      ],
      body: createdBill.map((bill, index) => [
        index + 1,
        bill.expenseName,
        bill.actualAmount,
        bill.quantity,
        (bill.actualAmount * bill.quantity).toFixed(2),
        bill.discount,
        bill.netAmount
      ]),
      foot: [
        [{ content: "Total", colSpan: 6, styles: { halign: 'right' } }, totalBill.toFixed(2)]
      ],
      theme: "grid",
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fontSize: 10,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: "center"
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      }
    });

    currentY = doc.lastAutoTable.finalY + 12;

    doc.setFont("helvetica", "bold");
    doc.text(`Amount Received: ${Number(amountReceived).toFixed(2)}`, margin, currentY);
    doc.text(`Outstanding Amount: ${Number(outstandingAmount).toFixed(2)}`, pageWidth - margin, currentY, { align: "right" });

    currentY += 8;
    doc.text(`Payment Type: ${paymentType || "-"}`, margin, currentY);

    const sigY = pageHeight - 30;
    doc.line(pageWidth - 70, sigY, pageWidth - 15, sigY);
    doc.setFontSize(10);
    doc.text("Authority Sign / Doctor Sign", pageWidth - 42.5, sigY + 5, { align: "center" });

    const userRole = authCtx?.role;
    const userName = authCtx?.userName;
    if (userRole === "ROLE_DOCTOR" && userName) {
      doc.setFontSize(8);
      doc.text(`( ${userName} )`, pageWidth - 42.5, sigY + 14, { align: "center" });
    }
  };

  const exportPDF = () => {
    if (!createdBill.length) {
      toast.error(t("No bill to export"));
      return;
    }
    setIsExporting(true);
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    buildPDF(doc);
    const patientName = `${selectedPatient?.firstName || ""}_${selectedPatient?.lastName || ""}`.trim();
    const safeName = patientName.replace(/\s+/g, "_") || "patient";
    doc.save(`${safeName}_bill.pdf`);
    setIsExporting(false);
  };

  return (
    <>
      {isLoading && <Loader />}
      <Modal
        show={patientData?.length > 0}
        onHide={onDataCloseHandler}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none' }}
        >
          <Modal.Title className="w-100 text-center text-white" style={{ color: 'white' }}>
            {t("Select Patient")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t("Patient Id")}</th>
                <th>{t("Name")}</th>
                <th>{t("Mobile")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.map((p) => (
                <tr key={p.patientId}>
                  <td>{p.patientId}</td>
                  <td>
                    {`${p.firstName || ""} ${p.middleName ? p.middleName + " " : ""}${p.lastName || ""}`.trim()}
                  </td>
                  <td>{p.mobile || "-"}</td>
                  <td>
                    <Button
                      className="btn-select-premium"
                      onClick={() => getSelectedPatientFromList(p)}
                    >
                      <FaCheck className="me-1" /> {t("Select")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
      <div className="container-fluid pt-1">
        <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center">
          <h5 className="textReports mb-0">{t("Create IPD Bill")}</h5>
        </div>
        <div className="bg-light p-3 rounded shadow-sm border mb-4 mx-0">
          <div className="row g-3 align-items-center">
            <div className="col-md-6 col-12">
              <form onSubmit={onSearchPatientHandler} className="d-flex w-100">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("Search Patient By Mobile Number")}
                  value={searchPara}
                  onChange={getSerachParaHandler}
                  required
                  style={{ border: "1px solid #ced4da", borderRadius: "4px 0 0 4px" }}
                />
                <button type="submit" className="btn-premium" style={{ borderRadius: "0 4px 4px 0" }}>
                  <FaSearch />
                </button>
              </form>
            </div>
            <div className="col-md-6 col-12">
              <div className="d-flex align-items-center justify-content-md-end gap-2">
                <label className="mb-0 fw-bold text-muted text-nowrap">{t("Billing Date")}:</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ maxWidth: "200px", border: "1px solid #ced4da" }}
                  value={billingDate instanceof Date ? billingDate.toISOString().split("T")[0] : ""}
                  onChange={(e) => {
                    const [year, month, day] = e.target.value.split("-");
                    let newDate = new Date(billingDate);
                    newDate.setFullYear(+year, +month - 1, +day);
                    setBillingDate(newDate);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-light p-3 rounded shadow-sm border mb-4">
          <div className="row g-3 align-items-end">
            {/* Expense Name with Searchable Dropdown */}
            <div className="col-md-4 col-6 position-relative">
              <label className="fw-bold small text-muted mb-1" htmlFor="expenseName">{t("Expense Name")}</label>
              <div className="input-group input-group-sm">
                <input
                  type="text"
                  id="expenseName"
                  className="form-control"
                  ref={expenseInput}
                  autoComplete="off"
                  placeholder={t("Select or Search...")}
                  style={{ border: '1px solid #ced4da' }}
                  value={expenseSearch}
                  onChange={(e) => onSearchChargeHandler(e.target.value)}
                  onFocus={() => setShowChargeDropdown(true)}
                />
                {expenseSearch && (
                  <span
                    className="input-group-text bg-white"
                    style={{ cursor: 'pointer', border: "1px solid #ced4da", borderLeft: 'none', borderRight: 'none' }}
                    onClick={() => {
                      setExpenseSearch("");
                      if (expenseInput.current) expenseInput.current.value = "";
                      setShowChargeDropdown(false);
                    }}
                  >
                    <FaTimes className="text-secondary" />
                  </span>
                )}
                <span
                  className="input-group-text bg-white"
                  style={{ cursor: 'pointer', border: "1px solid #ced4da", borderLeft: 'none' }}
                  onClick={() => setShowChargeDropdown(!showChargeDropdown)}
                >
                  <i className={`fa fa-caret-${showChargeDropdown ? 'up' : 'down'}`} />
                </span>
              </div>

              {showChargeDropdown && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-sm overflow-auto"
                  style={{ zIndex: 1000, maxHeight: '200px', top: '100%' }}
                >
                  {filteredCharges.length === 0 ? (
                    <div className="p-2 text-muted small">{t("No matches found")}</div>
                  ) : (
                    filteredCharges.map((c, idx) => (
                      <div
                        key={idx}
                        className="p-2 border-bottom small text-start"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        onClick={() => handleChargeSelect(c)}
                      >
                        {c.chargeName} ( <i className="fa fa-inr me-1"></i>{c.amount} )
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="col-md-1 col-6">
              <label className="fw-bold small text-muted mb-1" htmlFor="quantity">{t("Qty")}</label>
              <input type="number" id="quantity" className="form-control" ref={quantityInput} min={1} defaultValue={1} style={{ border: '1px solid #ced4da' }} />
            </div>
            <div className="col-md-2 col-6">
              <label className="fw-bold small text-muted mb-1" htmlFor="amount">{t("Amount")}</label>
              <input type="number" id="amount" className="form-control" ref={amountInput} style={{ border: '1px solid #ced4da' }} />
            </div>
            <div className="col-md-2 col-6">
              <label className="fw-bold small text-muted mb-1" htmlFor="discount">{t("Disc(%)")}</label>
              <input type="number" id="discount" className="form-control" ref={discountInput} style={{ border: '1px solid #ced4da' }} />
            </div>
            <div className="col-md-3 col-12">
              <button onClick={onSubmitBillHandler} className="btn-premium w-100">
                <FaPlus className="me-2" /> {t("Add Item")}
              </button>
            </div>
          </div>
        </div>

        <hr />

        {selectedPatient?.patientId && (
          <div className="row mb-3">
            <div className="col-md-3"><strong>{t("Patient Id")}:</strong> {selectedPatient.patientId}</div>
            <div className="col-md-5">
              <strong>{t("Name")}:</strong> {[selectedPatient.firstName, selectedPatient.middleName, selectedPatient.lastName].filter(Boolean).join(" ")}
            </div>
            <div className="col-md-4">
              <strong>{t("Outstanding Amount")}:</strong> {patientOutstanding !== null && patientOutstanding !== undefined ? patientOutstanding : "--"} <i className="fa fa-inr me-1"></i>
            </div>
          </div>
        )}

        <div className="table-responsive mt-1">
          <Table striped bordered hover size="sm" className="small">
            <thead>
              <tr className="bg-light">
                <th className="py-1">{t("Sr.")}</th>
                <th className="py-1">{t("Date")}</th>
                <th className="py-1">{t("Expense Name")}</th>
                <th className="py-1">{t("Amount")}</th>
                <th className="py-1">{t("Qty")}</th>
                <th className="py-1">{t("Total")}</th>
                <th className="py-1">{t("Disc")}</th>
                <th className="py-1">{t("Net")}</th>
                <th className="py-1">{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {createdBill.length === 0 && (
                <tr><td colSpan="9" className="text-center">{t("No Record found")}</td></tr>
              )}
              {createdBill.map((bill, index) => (
                <tr key={bill.id}>
                  <td>{bill.id}</td>
                  <td>{bill.date instanceof Date ? bill.date.toLocaleDateString() : bill.date}</td>
                  <td>{bill.expenseName}</td>
                  <td>{bill.actualAmount}</td>
                  <td>{bill.quantity}</td>
                  <td>{(bill.actualAmount * bill.quantity).toFixed(2)}</td>
                  <td>{bill.discount}%</td>
                  <td>{bill.netAmount}</td>
                  <td>
                    <button className="btn btn-sm btn-link text-danger p-0 ms-2" onClick={() => removeBillItem(index)}><FaTrash /></button>
                  </td>
                </tr>
              ))}
              {createdBill.length > 0 && (
                <tr>
                  <td colSpan="6"></td>
                  <td className="text-center"><strong>{t("Total")}</strong></td>
                  <td className="text-center">{totalBill.toFixed(2)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {createdBill.length > 0 && (
          <div className="bg-light p-2 rounded border">
            <div className="row g-2 align-items-end">
              <div className="col-md-2 col-6">
                <label className="small fw-bold">{t("Amount Received")}</label>
                <input type="number" className="form-control form-control-sm" value={amountReceived} onChange={onAmountReceivedChange} />
              </div>
              <div className="col-md-2 col-6">
                <label className="small fw-bold">{t("Outstanding")}</label>
                <input type="number" className="form-control form-control-sm" value={outstandingAmount} readOnly />
              </div>
              <div className="col-md-2 col-6">
                <label className="small fw-bold">{t("Payment Type")}</label>
                <select className="form-select form-select-sm themed-select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                  <option value="cash">{t("Cash")}</option>
                  <option value="card">{t("Card")}</option>
                  <option value="upi">{t("UPI")}</option>
                </select>
              </div>
              {paymentType !== "cash" && (
                <div className="col-md-2 col-6">
                  <label className="small fw-bold">{t("Txn No")}</label>
                  <input type="text" className="form-control form-control-sm" value={txnNo} onChange={(e) => setTxnNo(e.target.value)} placeholder={t("Txn No")} />
                </div>
              )}
              <div className="col-md-4 col-12">
                <label className="small fw-bold">{t("Note")}</label>
                <input type="text" className="form-control form-control-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Enter Note")} />
              </div>
            </div>
          </div>
        )}

        {createdBill.length > 0 && (
          <div className="mt-4 bg-light p-3 rounded shadow-sm border mx-0">
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
              {/* {selectedPatient?.patientId ? (
                <BillReceipt
                  patientData={selectedPatient}
                  createdBill={createdBill}
                  totalBill={totalBill}
                  amountReceived={amountReceived}
                  outstandingAmount={outstandingAmount}
                  billingDate={billingDate}
                  paymentType={paymentType}
                  txnNo={txnNo}
                  authCtx={authCtx}
                  billId={savedBillId}
                />
              ) : (
                <Button className="btn-premium" onClick={() => toast.error(t("Please select patient"))}>
                  <FaPrint className="me-2" /> {t("Print")}
                </Button>
              )}

              <Button onClick={exportPDF} className="btn-export-premium" disabled={isExporting}>
                <FaFileExport className="me-2" /> {isExporting ? t("EXPORTING...") : t("Export")}
              </Button>

              <Button
                onClick={() =>
                  shareBillViaWhatsApp(
                    selectedPatient,
                    createdBill || [],
                    totalBill,
                    amountReceived,
                    outstandingAmount,
                    billingDate,
                    paymentType,
                    txnNo || null,
                    authCtx,
                    savedBillId
                  )
                }
                className="btn-premium"
                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
              >
                <FaWhatsapp className="me-2" /> {t("WhatsApp")}
              </Button> */}


              {/* {savedBillId && (
                <Button onClick={resetForm} className="btn-outline-secondary-premium">
                  <FaPlus className="me-2" /> {t("New Bill")}
                </Button>
              )} */}

              <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2">
                <div className="form-check mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="printAfterSave"
                    checked={printAfterSave}
                    onChange={(e) => setPrintAfterSave(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label className="form-check-label small fw-bold text-muted" htmlFor="printAfterSave" style={{ cursor: 'pointer' }}>
                    {t("Print After Save")}
                  </label>
                </div>
                <Button onClick={onSaveBillHandler} disabled={isSubmitting} className="btn-premium">
                  <FaSave className="me-2" /> {isSubmitting ? t("Saving...") : t("Save")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewBill;
